import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import {
  History,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  Calendar,
  IndianRupee,
  Clock,
  Tag,
  AlertCircle,
  CalendarDays,
  Search,
  Pencil
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function BrandedPriceHistory({ product, token, onClose, onPriceUpdate }) {
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newPrice, setNewPrice] = useState("");
  const [newMrp, setNewMrp] = useState(""); // MRP field
  const [effectiveFromDate, setEffectiveFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [effectiveToDate, setEffectiveToDate] = useState(""); // Optional end date
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editMrp, setEditMrp] = useState("");
  const [editEffectiveFrom, setEditEffectiveFrom] = useState("");
  const [editEffectiveTo, setEditEffectiveTo] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const isBranded = product?.branded_mrp && 
    String(product.branded_mrp).trim() !== "" && 
    String(product.branded_mrp).trim() !== "0";

  // Set default MRP from product when dialog opens
  const handleOpenAddDialog = () => {
    setNewMrp(product?.branded_mrp || "");
    setShowAddDialog(true);
  };

  // Fetch price history
  const fetchPriceHistory = async () => {
    if (!product?.id) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/products/${product.id}/price-history`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.price_history || []);
      } else {
        toast.error("Failed to fetch price history");
      }
    } catch (error) {
      console.error("Error fetching price history:", error);
      toast.error("Error loading price history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriceHistory();
  }, [product?.id]);

  // Add new price
  const handleAddPrice = async () => {
    if (!newPrice || !effectiveFromDate) {
      toast.error("Please enter price and effective from date");
      return;
    }

    const priceValue = parseFloat(newPrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    // Parse MRP if provided
    const mrpValue = newMrp ? parseFloat(newMrp) : null;
    if (newMrp && (isNaN(mrpValue) || mrpValue <= 0)) {
      toast.error("Please enter a valid MRP");
      return;
    }

    // Validate date range if both dates provided
    if (effectiveToDate && effectiveFromDate >= effectiveToDate) {
      toast.error("Effective To date must be after Effective From date");
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(
        `${BACKEND_URL}/api/products/${product.id}/update-branded-price`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            new_price: priceValue,
            mrp: mrpValue, // Include MRP
            effective_from: effectiveFromDate,
            effective_to: effectiveToDate || null, // Optional end date
            notes: notes || `Price ₹${priceValue}, MRP ₹${mrpValue || 'N/A'}`
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Price updated successfully");
        setShowAddDialog(false);
        setNewPrice("");
        setNewMrp("");
        setEffectiveToDate("");
        setNotes("");
        fetchPriceHistory();
        if (onPriceUpdate) onPriceUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update price");
      }
    } catch (error) {
      console.error("Error updating price:", error);
      toast.error("Error updating price");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete history entry
  const handleDeleteEntry = async (historyId) => {
    if (!confirm("Are you sure you want to delete this price history entry?")) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/products/${product.id}/price-history/${historyId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success("Price history entry deleted");
        fetchPriceHistory();
      } else {
        toast.error("Failed to delete entry");
      }
    } catch (error) {
      toast.error("Error deleting entry");
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditPrice(entry.price?.toString() || "");
    setEditMrp(entry.mrp?.toString() || "");
    setEditEffectiveFrom(entry.effective_from || "");
    setEditEffectiveTo(entry.effective_to || "");
    setEditNotes(entry.notes || "");
    setShowEditDialog(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editPrice || !editEffectiveFrom) {
      toast.error("Price and Effective From date are required");
      return;
    }

    const priceValue = parseFloat(editPrice);
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    const mrpValue = editMrp ? parseFloat(editMrp) : null;
    if (editMrp && (isNaN(mrpValue) || mrpValue <= 0)) {
      toast.error("Please enter a valid MRP");
      return;
    }

    try {
      setSubmitting(true);
      const url = `${BACKEND_URL}/api/products/${product.id}/price-history/${editingEntry.id}`;
      console.log("Update URL:", url);
      console.log("Product ID:", product.id);
      console.log("Entry ID:", editingEntry.id);
      
      const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            price: priceValue,
            mrp: mrpValue,
            effective_from: editEffectiveFrom,
            effective_to: editEffectiveTo || null,
            notes: editNotes
          })
        }
      );

      if (response.ok) {
        toast.success("Price history entry updated");
        setShowEditDialog(false);
        setEditingEntry(null);
        fetchPriceHistory();
        if (onPriceUpdate) onPriceUpdate();
      } else {
        const error = await response.json();
        console.error("Update error response:", error);
        toast.error(error.detail || "Failed to update entry");
      }
    } catch (error) {
      console.error("Error updating entry:", error);
      toast.error("Error updating entry");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate price trend
  const getPriceTrend = () => {
    if (priceHistory.length < 2) return null;
    const sorted = [...priceHistory].sort((a, b) => 
      new Date(a.effective_from) - new Date(b.effective_from)
    );
    const oldest = sorted[0]?.price || 0;
    const newest = sorted[sorted.length - 1]?.price || 0;
    const change = ((newest - oldest) / oldest * 100).toFixed(1);
    return { change, direction: newest > oldest ? "up" : "down" };
  };

  const trend = getPriceTrend();

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-orange-500" />
            Price History - {product?.product_name}
          </DialogTitle>
          <DialogDescription>
            {isBranded ? (
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                  <Tag className="w-3 h-3 mr-1" />
                  Branded Product
                </Badge>
                <span className="text-sm text-gray-500">
                  Current MRP: ₹{product.branded_mrp}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-2 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">
                  This product does not have branded_mrp set. Add branded_mrp to enable price history tracking.
                </span>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Current Price & MRP Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <div className="flex justify-between items-center">
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-gray-500">Current Price</p>
                <p className="text-3xl font-bold text-gray-900 flex items-center">
                  <IndianRupee className="w-6 h-6" />
                  {product?.price || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Current MRP</p>
                <p className="text-2xl font-semibold text-orange-600 flex items-center">
                  <IndianRupee className="w-5 h-5" />
                  {product?.branded_mrp || 'N/A'}
                </p>
              </div>
            </div>
            {trend && (
              <div className={`flex items-center gap-1 ${
                trend.direction === "up" ? "text-red-600" : "text-green-600"
              }`}>
                {trend.direction === "up" ? (
                  <TrendingUp className="w-5 h-5" />
                ) : (
                  <TrendingDown className="w-5 h-5" />
                )}
                <span className="text-lg font-semibold">{trend.change}%</span>
                <span className="text-sm text-gray-500">overall</span>
              </div>
            )}
          </div>
        </div>

        {/* Add New Price Button */}
        {isBranded && (
          <div className="flex justify-end">
            <Button 
              onClick={handleOpenAddDialog}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Price Entry
            </Button>
          </div>
        )}

        {/* Price History Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[100px]">Price (₹)</TableHead>
                <TableHead className="w-[100px]">MRP (₹)</TableHead>
                <TableHead>Effective From</TableHead>
                <TableHead>Effective To</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[70px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading price history...
                  </TableCell>
                </TableRow>
              ) : priceHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No price history recorded yet.
                    {isBranded && " Click 'Add Price Entry' to add the first entry."}
                  </TableCell>
                </TableRow>
              ) : (
                priceHistory.map((entry, index) => (
                  <TableRow 
                    key={entry.id}
                    className={index === 0 && !entry.effective_to ? "bg-green-50" : ""}
                  >
                    <TableCell className="font-semibold">
                      <div className="flex items-center">
                        <IndianRupee className="w-4 h-4 text-gray-400" />
                        {entry.price}
                        {!entry.effective_to && (
                          <Badge className="ml-2 bg-green-100 text-green-700 text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-orange-600 font-medium">
                      {entry.mrp ? (
                        <div className="flex items-center">
                          <IndianRupee className="w-3 h-3 text-orange-400" />
                          {entry.mrp}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {entry.effective_from}
                      </div>
                    </TableCell>
                    <TableCell>
                      {entry.effective_to ? (
                        <span className="text-sm text-gray-500">
                          {entry.effective_to}
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          Present
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[150px] truncate" title={entry.notes || ""}>
                      {entry.notes || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(entry)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          title="Edit entry"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Add New Price Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-500" />
              Update Price
            </DialogTitle>
            <DialogDescription>
              Add a new price for {product?.product_name}. The old price will be archived.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Price and MRP - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>New Price (₹) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter price"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {product?.price && newPrice && (
                  <p className="text-xs text-gray-500">
                    Change: ₹{product.price} → ₹{newPrice}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>MRP (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter MRP"
                    value={newMrp}
                    onChange={(e) => setNewMrp(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {product?.branded_mrp && newMrp && newMrp !== product.branded_mrp && (
                  <p className="text-xs text-orange-500">
                    MRP Change: ₹{product.branded_mrp} → ₹{newMrp}
                  </p>
                )}
              </div>
            </div>

            {/* Date Range - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  Effective From *
                </Label>
                <DMYDateInput value={effectiveFromDate} onChange={(e) => setEffectiveFromDate(e.target.value)} max="2099-12-31" min="2015-01-01" />
                <p className="text-xs text-gray-400">
                  Can be past date (e.g., 2019-01-01)
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  Effective To (Optional)
                </Label>
                <DMYDateInput value={effectiveToDate} onChange={(e) => setEffectiveToDate(e.target.value)} max="2099-12-31" min={effectiveFromDate || "2015-01-01"} placeholder="Leave empty for current" />
                <p className="text-xs text-gray-400">
                  Leave empty if still current
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="e.g., Q2 2020 price revision"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Helper text for historical data entry */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium mb-1">💡 Adding Historical Prices</p>
              <p className="text-xs text-blue-600">
                For past orders calculation, add prices with their actual date ranges.
                Example: March 2020 price = ₹1000, From: 2020-03-01, To: 2020-05-31
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddPrice} 
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? "Updating..." : "Add Price Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Price History Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Price Entry
            </DialogTitle>
            <DialogDescription>
              Edit price history entry for {product?.product_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Price and MRP - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (₹) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter price"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>MRP (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter MRP"
                    value={editMrp}
                    onChange={(e) => setEditMrp(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            {/* Date Range - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  Effective From *
                </Label>
                <DMYDateInput value={editEffectiveFrom} onChange={(e) => setEditEffectiveFrom(e.target.value)} max="2099-12-31" min="2015-01-01" />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  Effective To
                </Label>
                <DMYDateInput value={editEffectiveTo} onChange={(e) => setEditEffectiveTo(e.target.value)} max="2099-12-31" min={editEffectiveFrom || "2015-01-01"} placeholder="Leave empty for current" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="e.g., Q2 2020 price revision"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              disabled={submitting}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Branded Products List Component
export function BrandedProductsList({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchBrandedProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/products/branded`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error("Error fetching branded products:", error);
      toast.error("Error loading branded products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandedProducts();
  }, []);

  const filteredProducts = products.filter(p => 
    p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.second_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Tag className="w-5 h-5 text-orange-500" />
            Branded Products
          </h2>
          <p className="text-sm text-gray-500">
            {products.length} products with price history tracking
          </p>
        </div>
        <div className="relative w-64">
          <Input
            placeholder="Search branded products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map(product => (
            <div 
              key={product.id}
              className="border rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer bg-white"
              onClick={() => setSelectedProduct(product)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">
                    {product.product_name}
                  </h3>
                  {product.second_name && (
                    <p className="text-sm text-gray-500 truncate">
                      {product.second_name}
                    </p>
                  )}
                </div>
                <Badge variant="outline" className="bg-orange-50 text-orange-600 ml-2 shrink-0">
                  <History className="w-3 h-3 mr-1" />
                  {product.price_history_count || 0}
                </Badge>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Current Price</p>
                  <p className="text-lg font-semibold flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {product.price || 0}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Branded MRP</p>
                  <p className="text-sm text-gray-600">₹{product.branded_mrp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Price History Modal */}
      {selectedProduct && (
        <BrandedPriceHistory
          product={selectedProduct}
          token={token}
          onClose={() => setSelectedProduct(null)}
          onPriceUpdate={fetchBrandedProducts}
        />
      )}
    </div>
  );
}

export default BrandedPriceHistory;
